import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ZoomradCard } from "@/api";
import { formatMaskedPan } from "@/lib/format";
import { Button, Card, Icon, ICONS } from "@/components/kit";

interface ZoomradCardCarouselProps {
  cards: ZoomradCard[];
  /** Tanlangan `cardId` (hech biri tanlanmagan bo'lsa null) */
  value: string | null;
  onChange: (cardId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Zoomrad kartalari — gorizontal snap-carousel (mobil to'lov ilovalari uslubida).
 *
 * Faqat Zoomrad in-app oqimida ko'rsatiladi: bu yerda to'lov turini emas,
 * KARTANI tanlaydi (provayder allaqachon Zoomrad). Tanlangan karta
 * `ChildDetailPage` da `ZoomradPayFee` ga uzatiladi.
 *
 * Holatlar: yuklanmoqda (skeleton) → xato (qayta urinish) → bo'sh → ro'yxat.
 */
export function ZoomradCardCarousel({
  cards,
  value,
  onChange,
  isLoading = false,
  error = null,
  onRetry,
  className,
}: ZoomradCardCarouselProps) {
  const { t } = useTranslation();

  return (
    <Card className={`overflow-hidden py-4 ${className ?? ""}`}>
      <h2 className="px-4 text-base font-semibold" style={{ color: "var(--c-text)" }}>
        {t("selectCardTitle")}
      </h2>

      {/* Kartalar bo'lsa — har doim ro'yxat (fondagi qayta yuklashda ham
          skeletonga sakramaydi). Aks holda: yuklanmoqda → xato → bo'sh. */}
      {cards.length > 0 ? (
        <CardScroller label={t("selectCardTitle")}>
          {cards.map((card) => (
            <CardTile
              key={card.cardId}
              card={card}
              selected={value === card.cardId}
              onSelect={() => onChange(card.cardId)}
            />
          ))}
        </CardScroller>
      ) : isLoading ? (
        <CardScroller>
          <CardSkeleton />
          <CardSkeleton />
        </CardScroller>
      ) : error ? (
        <StateBlock
          title={t("cardsErrorTitle")}
          hint={t("cardsErrorHint")}
          action={
            onRetry && (
              <Button variant="secondary" onClick={onRetry}>
                {t("cardsRetry")}
              </Button>
            )
          }
        />
      ) : (
        <StateBlock title={t("cardsEmptyTitle")} hint={t("cardsEmptyHint")} />
      )}
    </Card>
  );
}

/* ── Gorizontal snap konteyner ── */
function CardScroller({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div
      role={label ? "radiogroup" : undefined}
      aria-label={label}
      className="mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-px-4 px-4 pb-1"
      style={{ scrollbarWidth: "none" }}
    >
      {children}
    </div>
  );
}

/* ── Bitta karta ── */
function CardTile({
  card,
  selected,
  onSelect,
}: {
  card: ZoomradCard;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className="flex h-[128px] w-[212px] shrink-0 snap-start flex-col justify-between rounded-2xl p-3.5 text-left transition-all active:scale-[0.98]"
      style={{
        background: selected ? "var(--c-brand-tint)" : "var(--c-elevated-1)",
        boxShadow: selected
          ? "inset 0 0 0 2px var(--primary)"
          : "inset 0 0 0 1px var(--c-line)",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <BankLogo url={card.bankLogoUrl} name={card.cardName} />
        <span
          className="grid size-5 shrink-0 place-items-center rounded-full transition-opacity"
          style={{
            opacity: selected ? 1 : 0,
            color: "var(--primary)",
          }}
          aria-hidden
        >
          <Icon name={ICONS.success} size={20} />
        </span>
      </div>

      <div className="min-w-0">
        <p
          className="truncate text-[15px] font-semibold tracking-[0.04em]"
          style={{ color: "var(--c-text)" }}
        >
          {formatMaskedPan(card.maskedPan)}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-xs font-semibold" style={{ color: "var(--c-text)" }}>
            {card.cardName}
          </p>
          {card.type && <CardTypeBadge type={card.type} />}
        </div>
      </div>
    </button>
  );
}

/**
 * Karta tarmog'i (uzcard/humo/visa/mastercard) rasmiy brend rangida —
 * kulrang neytral pill o'rniga, to'lov ilovalaridagidek tanish gradient belgi.
 */
const CARD_TYPE_GRADIENTS: Record<string, string> = {
  uzcard: "linear-gradient(135deg, #2AA7DE 0%, #0F6FB0 100%)",
  humo: "linear-gradient(135deg, #F6A623 0%, #EF5DA8 100%)",
  visa: "linear-gradient(135deg, #2B3990 0%, #12173A 100%)",
  mastercard: "linear-gradient(135deg, #EB001B 0%, #F79E1B 100%)",
};

function CardTypeBadge({ type }: { type: string }) {
  const key = type.trim().toLowerCase();
  const gradient = CARD_TYPE_GRADIENTS[key];

  return (
    <span
      className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.04em]"
      style={
        gradient
          ? { background: gradient, color: "#fff" }
          : { background: "var(--c-elevated-2)", color: "var(--c-text-tertiary)" }
      }
    >
      {type}
    </span>
  );
}

/* ── Bank logosi (yuklanmasa — nom bosh harfi) ── */
function BankLogo({ url, name }: { url: string; name: string }) {
  const [failed, setFailed] = useState(false);

  if (!url || failed) {
    return (
      <span
        className="grid size-9 shrink-0 place-items-center rounded-xl text-sm font-bold text-white"
        style={{
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--primary) 85%, white) 0%, var(--primary) 100%)",
          boxShadow:
            "0 3px 8px -1px color-mix(in srgb, var(--primary) 45%, transparent), inset 0 0 0 1px color-mix(in srgb, white 20%, transparent)",
        }}
        aria-hidden
      >
        {(name || "•").trim().charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    // Zoomrad'ning card-list logotiplari (mas. Aloqa-Bank.png) — shaffof fonda
    // FAQAT OQ rangli belgi+matndan iborat (bankning o'z brend foni PNG ichida
    // yo'q). Shuning uchun OQ chip ustida ham, ilova och (light) mavzusida ham
    // logotip yo'qolib qoladi. Yechim: doim QORONG'I, mavzudan mustaqil chip —
    // oq chizgi qaysi fonga (dark/light) tushmasin har doim ko'rinadi.
    <span
      className="flex h-7 max-w-[85px] shrink-0 items-center overflow-hidden rounded-lg px-2"
      style={{
        background: "linear-gradient(135deg, #2b2f38 0%, #14161b 100%)",
        boxShadow:
          "0 3px 8px -1px rgba(0, 0, 0, 0.35), inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
      }}
    >
      {/* Logotip odatda belgi+bank nomidan iborat KENG rasm — kvadrat
          konteynerga sig'dirilsa nom o'qib bo'lmas darajada kichrayadi.
          Shuning uchun balandligi qattiq, kengligi tabiiy nisbatda (auto). */}
      <img
        src={url}
        alt=""
        className="h-6 w-auto max-w-full object-contain"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

/* ── Skeleton / bo'sh / xato holatlari ── */
function CardSkeleton() {
  return (
    <div
      className="h-[128px] w-[212px] shrink-0 animate-pulse rounded-2xl"
      style={{ background: "var(--c-skeleton-bg)" }}
      aria-hidden
    />
  );
}

function StateBlock({
  title,
  hint,
  action,
}: {
  title: string;
  hint: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 pt-4 text-center">
      <p className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
        {title}
      </p>
      <p className="text-xs" style={{ color: "var(--c-text-muted)" }}>
        {hint}
      </p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
